import { Http } from '@mygpt/app/http';

export interface JobParams {
  lang: string;
}

// 定义部门接口
interface Department {
  id: string;
  name: string;
  code: string;
  description: string;
  companyId: string;
  tenantId: number;
  managerId: string;
  parentId: number;
  enabled: boolean;
  createAt: string;
  updateAt: string;
}

// 定义 API 返回的数据格式接口
interface GetDepartmentResponse {
  success: boolean;
  outputParameters: Record<string, any>;
  fieldErrors: any[];
  departments: Department[];
}

// 定义用户接口
interface User {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  createAt: string;
  updateAt: string;
}

// 定义 API 返回的数据格式接口
interface GetDepartmentUsersResponse {
  success: boolean;
  outputParameters: Record<string, any>; // 如果 outputParameters 是一个空对象
  fieldErrors: any[]; // 如果 fieldErrors 是一个空数组
  departmentIdToUserIdsMap: Record<string, string[]>; // 部门ID到用户ID的映射
  noneDepartmentUserIds: string[]; // 没有分配部门的用户ID
  users: User[]; // 用户列表
}

// const API_BASE_URL = 'https://aliyun.gbase.ai/api/ogs';
const API_BASE_URL = '/ogs';

/**
 * 获取Admin用户信息
 * @param params
 * @returns
 */
export const getUserInfoForAdmin = ({ email, password }: { email: string; password: string }) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/login/password`, { email, password });
};

/**
 * 获取当前账户公司
 * @param params
 * @returns
 */
export const getMyCompanies = () => {
  const { get } = Http.use();
  return get<any>(`${API_BASE_URL}/user/my/companies`);
};

/**
 * 获取指定用户公司
 * @param params
 * @returns
 */
// export const getUserCompanies = (userId: string) => {
//   const { get } = Http.use();
//   return get<any>(`${API_BASE_URL}/user/${userId}/companies`);
// };

/**
 * 获取职位角色
 * @param params
 * @returns
 */
export const getJobRoles = ({ lang }: JobParams) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/config/job/roles`, { lang });
};

/**
 * 获取职位职务
 * @param params
 * @returns
 */
export const getJobPositions = ({ lang }: JobParams) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/config/job/positions`, { lang });
};

/**
 * 获取指定公司部门用户
 * @param params
 * @returns
 */
export const getDepartmentUsers = (companyId: string) => {
  const { get } = Http.use();
  return get<any>(`${API_BASE_URL}/user/company/${companyId}/department/users`);
};

/**
 * 创建部门
 * @param params
 * @returns
 */
export const createDepartment = ({
  name,
  code,
  description,
  parentId,
  companyId,
}: {
  name: string;
  code: string;
  description: string;
  parentId: number;
  companyId: number;
}) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/department/create`, { name, code, description, parentId, companyId });
};

/**
 * 更新部门
 * @param params
 * @returns
 */
export const updateDepartment = ({
  id,
  name,
  code,
  description,
}: {
  id: string;
  name: string;
  code: string;
  description: string;
}) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/department/update`, { id, name, code, description });
};

/**
 * 获取指定公司部门
 * @param params
 * @returns
 */
export const getDepartmentByCompanyId = (companyId: string): Promise<GetDepartmentResponse> => {
  const { get } = Http.use();
  return get<GetDepartmentResponse>(`${API_BASE_URL}/department/inherited/company/${companyId}`).then((response) => {
    return response.data;
  });
};

/**
 * 获取指定公司部门用户
 * @param params
 * @returns
 */
export const getDepartmentUsersByCompanyId = (companyId: string): Promise<GetDepartmentUsersResponse> => {
  const { get } = Http.use();
  return get<GetDepartmentUsersResponse>(`${API_BASE_URL}/user/company/${companyId}/department/users`).then(
    (response) => response.data
  );
};

/**
 * 创建用户
 * @param params
 * @returns
 */
export const createUser = ({
  name,
  email,
  password,
  companyId,
}: {
  name: string;
  email: string;
  password: string;
  companyId: string;
}) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/create`, { name, email, password, companyId });
};

/**
 * 获取用户列表
 * @param params
 * @returns
 */
export const getUserList = ({
  currentPage,
  pageSize,
  companyId,
  inCompanyUserNameInfixLike,
  enabled = true,
  userCompanyEnabled = true,
}: {
  currentPage: number;
  pageSize: number;
  companyId: string;
  inCompanyUserNameInfixLike?: string;
  enabled?: boolean;
  userCompanyEnabled?: boolean;
}) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/search/page`, {
    currentPage,
    pageSize,
    companyId,
    inCompanyUserNameInfixLike,
    enabled,
    userCompanyEnabled,
  });
};

/**
 * 获取公司信息
 * @param params
 * @returns
 */
export const getCompanyProfileInfo = (companyId: string) => {
  const { get } = Http.use();
  return get<any>(`${API_BASE_URL}/company/${companyId}/profile`);
};

/**
 * 获取当前用户权限
 * @param params
 * @returns
 */
export const getUserAuthority = (companyId: string) => {
  const { get } = Http.use();
  return get<any>(`${API_BASE_URL}/user/company/${companyId}/my/authority/`);
};

/**
 * 获取公司设置
 * @param params
 * @returns
 */
export const getCompanySetting = (companyId: string) => {
  const { get } = Http.use();
  return get<any>(`${API_BASE_URL}/company/${companyId}/setting`);
};

/**
 * 移除用户公司
 * @param params
 * @returns
 */
export const unsetUserCompany = ({ userId, companyId }: { userId: string; companyId: string }) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/company/unset`, { userId, companyId });
};

/**
 * 编辑用户
 * @param params
 * @returns
 */
export const editUser = ({ userId, companyId, userName }: { userId: string; companyId: string; userName: string }) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/company`, { userId, companyId, userName });
};

/**
 * 更新公司信息
 * @param params
 * @returns
 */
export const updateCompany = ({
  id,
  name,
  description,
  initialPassword,
}: {
  id: string;
  name: string;
  description: string;
  initialPassword: string;
}) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/company/update`, { id, name, description, initialPassword });
};

/**
 * 删除部门
 * @param params
 * @returns
 */
export const deleteDepartment = ({ id }: { id: string }) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp<any>(`${API_BASE_URL}/department/${id}`);
};

/**
 * 设置用户部门
 * @param params
 * @returns
 */
export const setUserDepartment = ({ userIds, departmentId }: { userIds: string[]; departmentId: string }) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/users/department/set`, { userIds, departmentId });
};

/**
 * 移除用户部门
 * @param params
 * @returns
 */
export const unsetUserDepartment = ({ userIds, departmentId }: { userIds: string[]; departmentId: string }) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/users/department/unset`, { userIds, departmentId });
};

/**
 * 修改用户密码
 * @param params
 * @returns
 */
export const changeUserPassword = ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/password/change`, { oldPassword, newPassword });
};

/**
 * 获取当前用户默认公司
 * @param params
 * @returns
 */
export const getMyCompanyDefault = () => {
  const { get } = Http.use();
  return get<any>(`${API_BASE_URL}/user/my/company/default`);
};

/**
 * 重置用户密码
 * @param params
 * @returns
 */
export const resetUserPassword = ({ id, password }: { id: string; password: string }) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/password/admin/update`, { id, password });
};

/**
 * 修改当前用户密码
 * @param params
 * @returns
 */
export const changeCurrentUserPassword = ({
  oldPassword,
  newPassword,
}: {
  oldPassword: string;
  newPassword: string;
}) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/password/change`, { oldPassword, newPassword });
};

/**
 * 用户启用
 * @param params
 * @returns
 */
export const setUserCompany = ({ userId, companyId }: { userId: string; companyId: string }) => {
  const { post } = Http.use();
  return post<any>(`${API_BASE_URL}/user/company/set`, { userId, companyId });
};
